import { SPECIALTY } from 'common/constants';

import { STEP_KEY } from '../utils';

export const PATIENT_FUNNEL_CONFIG = 'patientFunnelConfig';
export const PATIENT_FUNNEL_STEPS = 'patientFunnelSteps';
export const PATIENT_FUNNEL = 'patientFunnel';
export const PATIENT_FUNNEL_MARKETING = 'patientFunnelMarketing';
export const PATIENT_FUNNEL_PREFILL_DATA = 'patientFunnelPrefillData';
export const PATIENT_FUNNEL_LP_EXPERIMENT_DATA =
  'patientFunnelLpExperimentData';

export const METADATA_PROPERTIES = {
  COUNT: 'count',
  SKIPPED: 'skipped',
};

export const mapToMatch = (offer, referralPromotion, user, marketing, data) => {
  const getStepData = (stepName) => data[stepName]?.data;

  return {
    specialty: SPECIALTY.dentist,
    location: getStepData(STEP_KEY.LOCATION),
    other: {
      alternate: false,
      attitude: getStepData(STEP_KEY.ATTITUDE),
      availability: 'specify',
      specificAvailabilities: getStepData(STEP_KEY.AVAILABILITY),
      criteria: getStepData(STEP_KEY.CRITERIA),
      dentalAnxiety: getStepData(STEP_KEY.ANXIETY),
      distanceWeight: 75,
      doctorGender: generateGender(data),
      goals: getStepData(STEP_KEY.GOALS),
      howSoon: getStepData(STEP_KEY.HOW_SOON),
      hasInsurance: getStepData(STEP_KEY.INSURANCE)?.hasInsurance,
      insurance: getStepData(STEP_KEY.INSURANCE) || {},
      insurancePlan: getStepData(STEP_KEY.INSURANCE)?.plan,
      isClinicUser: (user?.editable?.clinics || []).length > 0,
      isPatient: (user?.editable?.patients || []).length > 0,
      lastVisited: getStepData(STEP_KEY.LAST_VISITED),
      location: getStepData(STEP_KEY.LOCATION),
      marketingConsent: getStepData(STEP_KEY.CONTACT),
      reason: getStepData(STEP_KEY.REASON_DETAILS)?.reason,
      reasonRaw: generateRawReason(data),
      subReasonIds: mapReasonsToApi(data)?.subReasonIds || null,
      subReasons: mapReasonsToApi(data)?.subReasons || [],
      givenReason: getStepData(STEP_KEY.REASON_DETAILS)?.urgentReason
        ?.additional,
      otherReason: getStepData(STEP_KEY.REASON_DETAILS)?.other,
      triageResponse: getStepData(STEP_KEY.REASON)?.triageResponse,
      specialistData: {
        specialistTypes: getStepData(STEP_KEY.SPECIALIST_TYPE)?.specialistTypes,
        hasReferral: getStepData(STEP_KEY.SPECIALIST_REFERRAL)?.hasReferral,
        noReferralSeeGeneralDentist: getStepData(STEP_KEY.SPECIALIST_REFERRAL)
          ?.seeGeneralDentist,
      },
      ongoingCare: getStepData(STEP_KEY.ONGOING_CARE)?.ongoingCare,
      selectedTreatments: getStepData(STEP_KEY.CONCERNS_TREATMENT)
        ?.concernsTreatment,
      userId: user?.id,
      affiliates: marketing?.affiliates || {},
      dmProps: generateDmProps(offer, referralPromotion, marketing),
      unbounceProps: marketing?.unbounceProps || {},
      utmTags: marketing?.utmTags || {
        source: '',
        medium: '',
        campaign: '',
        content: '',
        term: '',
      },
    },
  };
};

export const getFunnelConfiguration = (funnelStorage, allConfigs) => {
  if (!funnelStorage.config) {
    return null;
  }

  const rawConfig = allConfigs.find((c) => c.id === funnelStorage.config.id);
  if (!rawConfig) {
    return null;
  }

  const config = {
    id: rawConfig.id,
    testId: funnelStorage.config.test,
    variant: funnelStorage.config.variant,
    trafficSplit: rawConfig.trafficSplit,
    steps: rawConfig.order.map((s) => s.name),
  };

  if (rawConfig.segment) {
    config.segment = {
      countries: [rawConfig.segment.country.name],
      localities: rawConfig.segment.locality,
    };
  }

  if (rawConfig.markets?.length) {
    config.markets = rawConfig.markets;
  }

  return config;
};

const generateGender = (data) => {
  const answer = data[STEP_KEY.GENDER]?.data;

  // API expects null in this case
  if (answer === 'not-important') {
    return null;
  }

  return answer;
};

const generateRawReason = (data) => {
  const answer = data[STEP_KEY.REASON_DETAILS]?.data || {};

  return {
    overall: {
      name: answer.reason,
      id: answer.reasonId,
    },
  };
};

const generateDmProps = (offer, referralPromotion, marketing) => {
  const dmProps = {};

  if (offer?.slug) {
    dmProps.promo_code = offer.slug;
  }

  if (referralPromotion) {
    dmProps.referral_promo_id = referralPromotion.id;
  }

  if (marketing?.dmProps) {
    dmProps.referral_code = marketing.dmProps.referral_code;
    dmProps.referral_promo = marketing.dmProps.referral_promo;
  }

  return dmProps;
};

const mapReasonsToApi = (data) => {
  const reasonDetails = data[STEP_KEY.REASON_DETAILS]?.data || {};
  const { treatments, urgentReason } = reasonDetails;

  if (treatments) {
    return {
      subReasonIds: treatments
        .map((treatment) => treatment.id)
        .filter((treatment) => treatment !== 'other'),
      subReasons: treatments.map((treatment) => treatment.title),
    };
  }

  if (urgentReason) {
    return {
      subReasonIds: null, // API doesn't require IDs for urgent reasons
      subReasons: [urgentReason.title], // API requires this field to be an array
    };
  }

  return null;
};
